import React, { useRef } from 'react';
import styled from 'styled-components';
import { Calendar, Plus } from 'lucide-react';
import colors from 'theme/colors';
import PosEventsTab from 'modules/restaurant/menu/tabs/PosEventsTab';
import { useUser } from 'context/user/User';
import useToast from 'hooks/useToast';
import ToastContainer from 'components/_UI/ToastsContainer';

export default function PosEventsPage() {
    const { user } = useUser();
    const slug = user?.establishments?.[0]?.slug;
    const { toasts, toast } = useToast();
    const tabRef = useRef(null);

    if (!slug) {
        return (
            <PageWrapper>
                <NoEstablishmentState>
                    <NoEstablishmentIcon><Calendar size={36} color='#999' /></NoEstablishmentIcon>
                    <NoEstablishmentTitle>Nenhum estabelecimento encontrado</NoEstablishmentTitle>
                    <NoEstablishmentHint>Crie um estabelecimento para gerenciar seus eventos.</NoEstablishmentHint>
                </NoEstablishmentState>
            </PageWrapper>
        );
    }

    return (
        <PageWrapper>
            <ToastContainer toasts={toasts} />

            <PageHeader>
                <HeaderLeft>
                    <HeaderIcon><Calendar size={20} color={colors.occasionalPurple} /></HeaderIcon>
                    <div>
                        <PageTitle>Eventos</PageTitle>
                        <PageSubtitle>Gerencie eventos e ingressos do seu estabelecimento</PageSubtitle>
                    </div>
                </HeaderLeft>
                <NewBtn onClick={() => tabRef.current?.openCreate()} aria-label='Novo evento'>
                    <Plus size={20} color='#fff' strokeWidth={2.5} />
                </NewBtn>
            </PageHeader>

            <TableCard>
                <PosEventsTab ref={tabRef} slug={slug} toast={toast} />
            </TableCard>
        </PageWrapper>
    );
}

const PageWrapper = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    background: ${colors.neutralBackground};
`;

const PageHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 16px 14px;
    border-radius: 14px 14px 0 0;
    background: ${colors.neutralLight};
    flex-shrink: 0;
`;

const HeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const HeaderIcon = styled.div`
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: ${colors.occasionalPurple}10;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`;

const PageTitle = styled.h1`
    font-size: 18px;
    font-weight: 700;
    color: ${colors.nightDark};
    margin: 0;
    line-height: 1.3;
`;

const PageSubtitle = styled.p`
    font-size: 13px;
    color: #666;
    margin: 2px 0 0;
`;

const NewBtn = styled.button`
    width: 44px;
    height: 44px;
    border-radius: 14px;
    background: ${colors.occasionalPurple};
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    transition: transform 0.15s, box-shadow 0.15s;
    &:hover { transform: scale(1.06); box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2); }
    &:active { transform: scale(0.96); }
    &:focus-visible { outline: 2px solid ${colors.occasionalPurple}; outline-offset: 3px; }
`;

const TableCard = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    background: #fff;
    border-radius: 0 0 14px 14px;
    box-shadow: 0 -1px 4px rgba(0, 0, 0, 0.04), 0 2px 12px rgba(0, 0, 0, 0.06);
    overflow: hidden;
    min-height: 0;
`;

const NoEstablishmentState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    padding: 40px 20px;
    text-align: center;
`;

const NoEstablishmentIcon = styled.div`
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: ${colors.neutralDark};
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;
`;

const NoEstablishmentTitle = styled.h2`
    font-size: 17px;
    font-weight: 700;
    color: ${colors.nightDark};
    margin: 0 0 6px;
`;

const NoEstablishmentHint = styled.p`
    font-size: 14px;
    color: #666;
    margin: 0;
    max-width: 280px;
`;
